import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import {
  serverTimestamp,
  addDoc,
  collection,
  doc,
  getDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export default function DepositTool() {
  const navigate = useNavigate();

  // UI state
  const [mode, setMode] = useState('booking'); // 'booking' | 'pi'
  const [bookingId, setBookingId] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [defaultHoldAmount, setDefaultHoldAmount] = useState(0); // dollars
  const [captureAmount, setCaptureAmount] = useState(''); // dollars, optional
  const [note, setNote] = useState('');

  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');

  // Load from bookingId → fill PI + default amount
  const loadFromBooking = async () => {
    if (!bookingId.trim()) {
      alert('Enter a Booking ID');
      return;
    }
    setIsProcessing(true);
    setMessage('');
    try {
      const snap = await getDoc(doc(db, 'bookings', bookingId.trim()));
      if (!snap.exists()) {
        setMessage('❌ Booking not found.');
        return;
      }
      const b = snap.data();
      const pi = b.depositHoldPaymentIntentId || '';
      const cents = Number(b.depositHoldAmountCents || 0);
      setPaymentIntentId(pi);
      setDefaultHoldAmount(cents > 0 ? cents / 100 : 0);
      if (!pi) {
        setMessage('⚠️ Booking has no deposit hold PaymentIntent saved.');
      } else {
        setMessage('✅ Loaded deposit hold from booking.');
      }
    } catch (e) {
      console.error(e);
      setMessage('❌ Failed to load booking.');
    } finally {
      setIsProcessing(false);
    }
  };

  const logAction = async (data) => {
    try {
      const localAdmin = JSON.parse(localStorage.getItem('user')) || {};
      const adminInfo = {
        email: localAdmin.email || 'unknown',
        uid: localAdmin.uid || 'unknown',
        name: localAdmin.name || 'Admin',
        role: localAdmin.role || 'admin',
      };

      const log = {
        ...data,
        note: note || null,
        triggeredBy: adminInfo,
        timestamp: serverTimestamp(),
        isoTime: new Date().toISOString(),
      };

      await addDoc(collection(db, 'depositLogs'), log);
      if (log.userId) {
        await addDoc(collection(db, `users/${log.userId}/depositHistory`), log);
      }
    } catch (e) {
      console.warn('⚠️ Failed to write deposit log:', e);
    }
  };

  const onCapture = async () => {
    if (!paymentIntentId.trim()) {
      alert('Enter a PaymentIntent ID (or load from Booking ID).');
      return;
    }
    setIsProcessing(true);
    setMessage('');
    try {
      const amountToCaptureCents =
        captureAmount.trim() === ''
          ? undefined // full capture
          : Math.max(0, Math.round(parseFloat(captureAmount) * 100));

      const fn = httpsCallable(functions, 'captureDepositHold');
      const res = await fn({
        paymentIntentId: paymentIntentId.trim(),
        amountToCaptureCents,
      });

      await logAction({
        action: 'capture',
        bookingId: bookingId || null,
        paymentIntentId: paymentIntentId.trim(),
        amountCaptured: amountToCaptureCents ?? Math.round(defaultHoldAmount * 100),
        userId: null, // fill if you attach renter/host later
        status: res?.data?.status || 'unknown',
      });

      setMessage(`✅ Captured ${amountToCaptureCents
        ? `$${(amountToCaptureCents / 100).toFixed(2)}`
        : 'full amount'} (status: ${res?.data?.status || 'unknown'})`);
    } catch (e) {
      console.error(e);
      setMessage('❌ Capture failed.');
    } finally {
      setIsProcessing(false);
    }
  };

  const onRelease = async () => {
    if (!paymentIntentId.trim()) {
      alert('Enter a PaymentIntent ID (or load from Booking ID).');
      return;
    }
    setIsProcessing(true);
    setMessage('');
    try {
      const fn = httpsCallable(functions, 'cancelDepositHold');
      const res = await fn({ paymentIntentId: paymentIntentId.trim() });

      await logAction({
        action: 'release',
        bookingId: bookingId || null,
        paymentIntentId: paymentIntentId.trim(),
        amountCaptured: 0,
        userId: null,
        status: res?.data?.status || 'unknown',
      });

      setMessage(`✅ Hold released (status: ${res?.data?.status || 'unknown'})`);
    } catch (e) {
      console.error(e);
      setMessage('❌ Release failed.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={styles.container}>
      

      <h2 style={styles.title}>🔒 Deposit Hold Tool</h2>
      <p style={styles.help}>
        Capture = take some or all of the held amount. Release = remove the hold.
        Holds expire automatically (Stripe capture window), so perform actions promptly.
      </p>

      <div style={styles.card}>
        <div style={styles.toggleRow}>
          <label>
            <input
              type="radio"
              name="mode"
              value="booking"
              checked={mode === 'booking'}
              onChange={() => setMode('booking')}
            />
            Lookup by Booking ID
          </label>
          <label style={{ marginLeft: '2rem' }}>
            <input
              type="radio"
              name="mode"
              value="pi"
              checked={mode === 'pi'}
              onChange={() => setMode('pi')}
            />
            Use PaymentIntent ID
          </label>
        </div>

        {mode === 'booking' ? (
          <div>
            <input
              type="text"
              placeholder="Booking ID"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              style={styles.input}
            />
            <button onClick={loadFromBooking} style={styles.secondaryBtn} disabled={isProcessing}>
              Load
            </button>
          </div>
        ) : null}

        <input
          type="text"
          placeholder="Deposit PaymentIntent ID"
          value={paymentIntentId}
          onChange={(e) => setPaymentIntentId(e.target.value)}
          style={styles.input}
        />

        <div style={styles.row}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Amount to Capture (optional)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={defaultHoldAmount ? `Default: $${defaultHoldAmount.toFixed(2)}` : 'e.g. 50.00'}
              value={captureAmount}
              onChange={(e) => setCaptureAmount(e.target.value)}
              style={styles.input}
            />
            <small style={{ color: '#6b7280' }}>
              Leave blank to capture full available hold.
            </small>
          </div>
        </div>

        <label style={styles.label}>Internal note (optional)</label>
        <input
          type="text"
          placeholder="Why are you capturing/releasing?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={styles.input}
        />

        <div style={styles.actions}>
          <button onClick={onCapture} disabled={isProcessing} style={styles.primaryBtn}>
            {isProcessing ? 'Capturing...' : 'Capture Hold'}
          </button>
          <button onClick={onRelease} disabled={isProcessing} style={styles.dangerBtn}>
            {isProcessing ? 'Releasing...' : 'Release Hold'}
          </button>
        </div>

        {message && <p style={styles.message}>{message}</p>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    backgroundColor: '#f9fafb',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#065f46',
    marginBottom: '0.25rem',
  },
  help: { color: '#374151', marginBottom: '1rem' },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '1rem',
    maxWidth: 640,
  },
  toggleRow: { display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '1.5rem' },
  row: { display: 'flex', gap: '1rem' },
  label: { display: 'block', fontWeight: 600, marginTop: '0.75rem', marginBottom: '0.25rem' },
  input: {
    display: 'block',
    width: '100%',
    marginBottom: '0.75rem',
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: 6,
    border: '1px solid #d1d5db',
  },
  actions: { display: 'flex', gap: '0.75rem', marginTop: '0.5rem' },
  primaryBtn: {
    backgroundColor: '#22c55e',
    color: '#fff',
    fontWeight: 700,
    padding: '0.5rem 1rem',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
  },
  dangerBtn: {
    backgroundColor: '#ef4444',
    color: '#fff',
    fontWeight: 700,
    padding: '0.5rem 1rem',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
  },
  secondaryBtn: {
    backgroundColor: '#e5e7eb',
    color: '#111827',
    fontWeight: 600,
    padding: '0.5rem 1rem',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    marginBottom: '0.75rem',
  },
  message: { marginTop: '1rem', fontWeight: 600, color: '#065f46' },
};
